import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LoadingQuestion, TimerSection, WaitingSpinner } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import { getGameName, calculateTimeRemaining } from '../../utils/gameUtils';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { gameUsesTeams } from '../../utils/teamGames';
import type { GameStatistics, Question } from '../../types/game';
import { HouseRunTaskCard } from './houseRun';
import type { HouseRunTask } from './houseRun/HouseRunTaskCard';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/game-screen.css';
import { useTranslation } from '../../i18n';

interface TeamSubmission {
  teamId: 'A' | 'B';
  playerName: string;
  photoData: string;
  task?: HouseRunTask | null;
  resolved?: boolean;
  accepted?: boolean;
}

interface HostQuestion {
  questionId?: string;
  id?: string;
  question: string;
  title?: string;
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  isHouseRun?: boolean;
  interesting?: string;
  taskA?: HouseRunTask;
  taskB?: HouseRunTask;
}

interface HouseRunHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { id: 'A'; name: string; score: number }; B: { id: 'B'; name: string; score: number } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: HostQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const emptySubs = (): Record<'A' | 'B', TeamSubmission | null> => ({ A: null, B: null });

const HouseRunHostDisplay: React.FC<HouseRunHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<HostQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState(roomState.currentQuestion?.timeLimit ?? 0);
  const [submissions, setSubmissions] = useState<Record<'A' | 'B', TeamSubmission | null>>(emptySubs());
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<
    Array<{ playerId: string; playerName: string; score: number }>
  >([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const currentQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id
  );
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setSubmissions(emptySubs());
      previousQuestionIdRef.current = undefined;
      currentQuestionIdRef.current = undefined;
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    const applyHostState = (data: {
      questionId: string;
      submissions?: Record<'A' | 'B', TeamSubmission | null>;
      taskA?: HouseRunTask;
      taskB?: HouseRunTask;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      if (data.taskA || data.taskB) {
        setCurrentQuestion((prev) =>
          prev
            ? {
                ...prev,
                taskA: data.taskA ?? prev.taskA,
                taskB: data.taskB ?? prev.taskB,
              }
            : prev
        );
      }
      if (data.submissions) {
        setSubmissions({
          A: data.submissions.A ?? null,
          B: data.submissions.B ?? null,
        });
      }
    };

    const handleQuestion = (data: HostQuestion & { questionId?: string }) => {
      const qId = data.questionId || data.id;
      if (qId && qId === previousQuestionIdRef.current) return;
      previousQuestionIdRef.current = qId;
      currentQuestionIdRef.current = qId;
      setSubmissions(emptySubs());
      setCurrentQuestion({ ...data, questionId: qId });
      setTimeRemaining(data.timeLimit);
      if (qId && roomState.roomName) {
        socket.emit('get-house-run-host-state', { roomName: roomState.roomName, questionId: qId });
      }
    };

    const handleHostState = (data: {
      questionId: string;
      submissions?: Record<'A' | 'B', TeamSubmission | null>;
      taskA?: HouseRunTask;
      taskB?: HouseRunTask;
    }) => {
      applyHostState(data);
    };

    const handleResolved = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      accepted: boolean;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setSubmissions((prev) => {
        const sub = prev[data.teamId];
        if (!sub) return prev;
        return {
          ...prev,
          [data.teamId]: { ...sub, resolved: true, accepted: data.accepted },
        };
      });
    };

    const handleGameEnded = (data: {
      leaderboard?: typeof finalLeaderboard;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setSubmissions(emptySubs());
      previousQuestionIdRef.current = undefined;
      currentQuestionIdRef.current = undefined;
    };

    socket.on('question', handleQuestion);
    socket.on('house-run-host-state', handleHostState);
    socket.on('house-run-team-resolved', handleResolved);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('house-run-host-state', handleHostState);
      socket.off('house-run-team-resolved', handleResolved);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, roomState.roomName]);

  // Only adopt a *new* question from room-state (avoid thrashing / wiping live photos).
  useEffect(() => {
    const q = roomState.currentQuestion;
    if (!q) return;

    const questionId = q.questionId || q.id;
    if (!questionId || questionId === previousQuestionIdRef.current) return;

    previousQuestionIdRef.current = questionId;
    currentQuestionIdRef.current = questionId;
    setSubmissions(emptySubs());
    setCurrentQuestion(q);
    setTimeRemaining(
      calculateTimeRemaining(
        q as Question,
        roomState.questionStartTime || q.startTime
      )
    );
    socket.emit('get-house-run-host-state', {
      roomName: roomState.roomName,
      questionId,
    });
  }, [roomState.currentQuestion, roomState.questionStartTime, roomState.roomName, socket]);

  useEffect(() => {
    if (!currentQuestion || gameEnded) return;
    const interval = setInterval(() => {
      const start = roomState.questionStartTime || currentQuestion.startTime;
      const remaining = Math.max(0, currentQuestion.timeLimit - (Date.now() - start));
      setTimeRemaining(remaining);
    }, 200);
    return () => clearInterval(interval);
  }, [currentQuestion, gameEnded, roomState.questionStartTime]);

  if (gameEnded) {
    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard;

    return (
      <GameEndScreen
        finalLeaderboard={teamLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={() => {
          socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
          setGameEnded(false);
        }}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  if (!currentQuestion && roomState.gameState === 'playing') {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  if (!currentQuestion) {
    return (
      <div className="app">
        <Header roomCode={roomState.roomName} />
        <WaitingSpinner message={t('gameplay.houseRun.waiting')} isLoading />
      </div>
    );
  }

  const themeTitle = currentQuestion.title || t('gameplay.houseRun.hostTitle');
  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = currentQuestion.timeLimit > 0 ? timeRemaining / currentQuestion.timeLimit : 0;

  const renderTeamPanel = (teamId: 'A' | 'B') => {
    const sub = submissions[teamId];
    const pendingTask = teamId === 'A' ? currentQuestion.taskA : currentQuestion.taskB;
    return (
      <div
        key={teamId}
        className="house-run-team-panel"
        style={{
          flex: 1,
          minWidth: 0,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '1rem',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          overflow: 'hidden',
        }}
      >
        <h3 style={{ margin: 0, textAlign: 'center', fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}>
          {t('gameplay.houseRun.teamPanel', { team: teamId })}
        </h3>

        {!sub && pendingTask && <HouseRunTaskCard task={pendingTask} teamId={teamId} />}

        {!sub && !pendingTask && (
          <p style={{ textAlign: 'center', opacity: 0.6, margin: 'auto 0', fontSize: '0.9rem' }}>
            {t('gameplay.houseRun.hunting')}
          </p>
        )}

        {sub && (
          <>
            <p style={{ textAlign: 'center', margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>
              {sub.playerName}
            </p>
            {(sub.task || pendingTask) && (
              <HouseRunTaskCard task={(sub.task || pendingTask)!} />
            )}
            <img
              src={sub.photoData}
              alt=""
              style={{
                width: '100%',
                maxHeight: 'min(36vh, 280px)',
                objectFit: 'contain',
                borderRadius: '0.5rem',
                background: 'rgba(0,0,0,0.25)',
              }}
            />
            {sub.resolved && (
              <p
                style={{
                  textAlign: 'center',
                  margin: 0,
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: sub.accepted ? '#22C55E' : '#F87171',
                }}
              >
                {sub.accepted ? t('gameplay.houseRun.accepted') : t('gameplay.houseRun.rejected')}
              </p>
            )}
            {!sub.resolved && (
              <p style={{ textAlign: 'center', margin: 0, opacity: 0.7, fontSize: '0.85rem' }}>
                {t('gameplay.houseRun.awaitingVotes')}
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="app">
      <Header
        leftButton={
          onBackToGameSelection
            ? {
                icon: HeaderIcons.back,
                onClick: onBackToGameSelection,
                tooltip: t('gameplay.backToGameSelection'),
              }
            : undefined
        }
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        centerContent={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5rem',
              flexWrap: 'wrap',
            }}
          >
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="game-header-label">{t('gameplay.partyHost.gameLabel')}</span>
              <span className="game-header-value">{getGameName(roomState.gameType)}</span>
            </div>
            <div className="game-header-divider header-game-name" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="game-header-label">{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.15rem',
                  color: 'var(--color-primary)',
                  padding: '0.3rem 0.8rem',
                  backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                  borderRadius: '6px',
                  border: '2px solid var(--color-primary)',
                }}
              >
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      <div className="game-host-layout">
        <div className={`game-host-left no-image ${!hostIsPlaying ? 'host-only' : ''}`}>
          <div className="game-host-timer-section">
            <TimerSection
              timeSeconds={timeSeconds}
              progress={progress}
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
            />
          </div>

          <div className="game-host-question-section" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <h1
              className="game-question-text"
              style={{
                color: 'var(--color-primary)',
                marginBottom: '0.5rem',
                textAlign: 'center',
                fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
              }}
            >
              {themeTitle}
            </h1>
            <p
              style={{
                textAlign: 'center',
                margin: '0 0 1rem',
                opacity: 0.75,
                fontSize: 'clamp(0.85rem, 1.5vw, 1rem)',
              }}
            >
              {t('gameplay.houseRun.hostHint')}
            </p>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                flex: 1,
                minHeight: 0,
                alignItems: 'stretch',
              }}
            >
              {renderTeamPanel('A')}
              {renderTeamPanel('B')}
            </div>
          </div>
        </div>

        <StandingsSidebar
          players={roomState.players}
          liveAnswers={[]}
          totalPlayers={roomState.players.length}
          questionId={currentQuestion.questionId || currentQuestion.id}
          mode={gameUsesTeams(roomState.gameType) ? 'teams' : 'players'}
          teams={roomState.teams}
          shouldUpdateScores
        />
      </div>
    </div>
  );
};

export default HouseRunHostDisplay;
